import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SIDEBAR_WIDTH } from '../../theme';
import { UserMenu } from '../auth/UserMenu';
import { useAuth } from '../../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { useListOrganizations, useSwitchActiveOrg } from '../../api/generated/generated';
import type { Organization } from '../../api/generated';

interface TopBarProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function TopBar({ onMenuClick, showMenuButton = false }: TopBarProps) {
  const { user, refreshToken } = useAuth();
  const queryClient = useQueryClient();
  const { data: orgs } = useListOrganizations({
    query: {
      select: (response): Organization[] => response.data,
    },
  });
  const switchOrg = useSwitchActiveOrg({
    mutation: {
      onSuccess: async () => {
        await refreshToken();
        queryClient.invalidateQueries();
      },
    },
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const currentOrg = orgs?.find((o) => o.id === user?.orgId);
  const showSwitcher = orgs && orgs.length > 1;

  const handleSwitch = async (orgId: string) => {
    setAnchorEl(null);
    if (orgId !== user?.orgId) {
      await switchOrg.mutateAsync({ data: { org_id: orgId } });
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: `calc(100% - ${SIDEBAR_WIDTH}px)` },
        ml: { sm: `${SIDEBAR_WIDTH}px` },
        bgcolor: 'background.paper',
        color: 'text.primary',
      }}
    >
      <Toolbar>
        {showMenuButton && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          Hanover Sales Coach
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {currentOrg && (
            <>
              <Button
                size="small"
                onClick={showSwitcher ? (e) => setAnchorEl(e.currentTarget) : undefined}
                endIcon={showSwitcher ? <ArrowDropDownIcon /> : undefined}
                sx={{ textTransform: 'none' }}
              >
                {currentOrg.name}
              </Button>
              {showSwitcher && (
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                >
                  {orgs?.map((org) => (
                    <MenuItem
                      key={org.id}
                      selected={org.id === user?.orgId}
                      onClick={() => handleSwitch(org.id)}
                    >
                      {org.name}
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}
          <UserMenu />
        </Box>
      </Toolbar>
    </AppBar>
  );
}
